/**
 * Raising an invoice — the one form, used by both screens that raise one.
 *
 * Extracted rather than copied. /admin/billing and the Owner-only Invoices
 * page both mount this, and both post to the same endpoint, so there is no
 * second path for the tax figures, the validation or the client-rate snapshot
 * to disagree along. A copied form is the failure this file exists to prevent:
 * the two would drift the first time either was touched, and the figures they
 * disagreed about would be on invoices already sent to clients.
 *
 * Nothing here computes tax. billing_tax.py does that server-side, from rates
 * somebody supplied, and this form neither invents a rate nor recomputes a
 * figure it was given.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Lock, X } from 'lucide-react';
import { client as api } from '../api/client';
import { money } from '../lib/money';
import { formatPhone } from '../lib/phone';
import type {
  Client,
  FeeBasis,
  GstRegistration,
  InvoiceStatus,
  Page,
  Placement,
} from '../types';
import { titleCaseName } from '../lib/titleCase';
import {
  OVERRIDABLE,
  OVERRIDDEN_RING,
  STATUS_TONE,
  statusLabel,
} from '../lib/invoiceStatus';
import ClientSelect from './ClientSelect';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function InvoiceForm({
  requirePlacement = false,
  title,
  onClose,
  snapshot,
  snapshotNote,
  initial,
  submitLabel = 'Create draft',
  submitting,
  figuresLocked = false,
  lockReason,
  payment,
  statusOverride,
  onSubmit,
}: {
  /**
   * Raised from the tracker view, which is the placement-billing view — so an
   * invoice raised there has to name the placement it bills for. The general
   * path keeps placement optional, because not everything billed is a
   * placement fee and that endpoint serves both.
   */
  requirePlacement?: boolean;
  /**
   * A heading and a close button, for the mounts that are popups.
   *
   * Both are optional because two of the four mounts are not popups at all —
   * /admin/billing and the blank form on this page render inline, below the
   * button that opened them, where there is nothing to close and the
   * surrounding page already says what the form is for.
   *
   * A popup needs both: an overlay with no title is a form that appeared from
   * nowhere, and one with no X is a form you can only leave by scrolling to
   * the bottom. The submit and cancel buttons down there still work; this is
   * the way out that does not require finding them first.
   */
  title?: string;
  onClose?: () => void;
  /**
   * The placement's own facts, shown but never editable.
   *
   * These are the fields the server copies onto the invoice when it is raised.
   * They are not inputs and must not become inputs: the point of snapshotting
   * is that the invoice records what was true at the moment it was raised
   * rather than whatever the candidate record says a year later. Shown here so
   * that raising is confirm-and-save rather than trust-and-save.
   */
  snapshot?: SnapshotFacts | null;
  /** Why that block cannot be edited, in words, for whoever wonders. */
  snapshotNote?: string;
  /**
   * Seed values, for editing an existing draft.
   *
   * The edit path mounts this same form rather than a second set of money
   * inputs, so the two cannot disagree about what an invoice is made of.
   * Absent when raising a new one, which starts empty.
   */
  initial?: Partial<{
    clientId: string;
    placementId: string;
    clientGstRegistrationId: string;
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string;
    amount: string;
    taxAmount: string;
    notes: string;
  }>;
  submitLabel?: string;
  submitting: boolean;
  /**
   * The money, locked. Set once the invoice has been raised: the client holds
   * the figures and the server refuses to move them, so an editable box would
   * be a control that can only fail.
   */
  figuresLocked?: boolean;
  lockReason?: string;
  /**
   * Recording a receipt from this same form, so that editing an invoice and
   * recording money against it are not two separate places.
   *
   * `enabled` is false for a draft, where the section is shown with its reason
   * rather than hidden. A field that simply vanishes teaches nobody why, and
   * "raise it first" is the part worth knowing.
   */
  payment?: {
    /** What is still outstanding, for the over-payment check. */
    outstanding: string;
    /** The server refuses a receipt dated before this. */
    invoiceDate: string;
    enabled: boolean;
    disabledReason?: string;
  } | null;
  /**
   * The status, and an administrator's ability to disagree with it.
   *
   * `status` is what the invoice reads as — the override where one is set, the
   * arithmetic otherwise. `derivedStatus` is what the payments imply, and it
   * is shown beside the other whenever the two differ: an overridden figure
   * that looks identical to a computed one is the version of this that does
   * harm, because the number moves and nothing says why.
   *
   * Absent on a form that is creating an invoice — there is no status to
   * override until there is a row.
   */
  statusOverride?: {
    status: InvoiceStatus;
    derivedStatus: InvoiceStatus | null;
    reason: string | null;
    overridden: boolean;
    saving: boolean;
    /** Null clears the override and hands the row back to the arithmetic. */
    onApply: (status: InvoiceStatus | null, reason: string) => void;
  } | null;
  /**
   * The invoice body, and a payment body when one was filled in.
   *
   * Two arguments rather than one merged object, because they are two requests
   * to two endpoints under two different rules. Callers that only ever create
   * an invoice take the first and ignore the second.
   */
  onSubmit: (
    body: Record<string, unknown>,
    payment: Record<string, unknown> | null,
  ) => void;
}) {
  /**
   * The override being composed, not the one in force.
   *
   * Blank rather than prefilled with the current override, for the reason
   * the payment boxes are blank: a control already holding a value invites
   * a save nobody meant, and here that save moves a receivable. Empty means
   * "leave it to the payments", which is also how an override is cleared.
   */
  const [overrideTo, setOverrideTo] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [form, setForm] = useState({
    clientId: '',
    placementId: '',
    clientGstRegistrationId: '',
    invoiceNumber: '',
    invoiceDate: today(),
    dueDate: '',
    amount: '',
    taxAmount: '0.00',
    notes: '',
    ...initial,
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // Blank, not prefilled to the outstanding. A box already holding a number is
  // one somebody saves without reading, and that would record a receipt that
  // never arrived. RecordPaymentDialog prefills because recording is the only
  // thing it does; here it is one optional half of the form.
  const [received, setReceived] = useState('');
  const [tdsWithheld, setTdsWithheld] = useState('');
  const [receivedOn, setReceivedOn] = useState(today);

  const settles = (Number(received) || 0) + (Number(tdsWithheld) || 0);
  const outstanding = Number(payment?.outstanding) || 0;
  // The rule the server applies, asked here to save a round trip to be
  // refused. Cash and withholding are checked together because together is how
  // they discharge the invoice.
  const overPaying = Boolean(payment?.enabled) && settles > outstanding + 0.005;
  const hasPayment = Boolean(payment?.enabled) && (Number(received) || 0) > 0;

  /**
   * The chosen client's own record, not a list to search.
   *
   * This used to fetch `/clients?limit=200`, which is both more than the form
   * needs and less than there are: the endpoint caps limit at 200 and there
   * are more clients than that, so the dropdown was an alphabetical prefix
   * that stopped silently. The picker below now reads /clients/options, and
   * the one field this form actually wants off the full record --
   * paymentTermsDays -- is fetched for the one client that was picked.
   */
  const selectedClientQuery = useQuery<Client>({
    queryKey: ['client', form.clientId],
    enabled: Boolean(form.clientId),
    queryFn: async () =>
      (await api.get(`/api/v1/clients/${form.clientId}`)).data,
  });
  const selectedClient =
    selectedClientQuery.data?.id === form.clientId
      ? selectedClientQuery.data
      : undefined;

  // Only this client's placements: the API refuses an invoice pointing at
  // another client's placement, so offering them would just produce a 422.
  const placements = useQuery<Page<Placement>>({
    queryKey: ['billing-placements', form.clientId],
    enabled: Boolean(form.clientId),
    queryFn: async () =>
      (await api.get('/api/v1/placements', {
        params: { clientId: form.clientId, limit: 200 },
      })).data,
  });

  /**
   * Payment terms give a sensible due date, once the client is known.
   *
   * One effect rather than a handler plus a seeding pass. Both routes into
   * this form -- picking a client by hand, or opening it from a placement with
   * the client already in `initial` -- now end in the same place: the client's
   * record arrives, and if the due date is blank it gets filled. The old
   * version needed a ref to stop the seeding pass reapplying terms over a
   * typed date; here the blank check does that on its own.
   *
   * Never over a value already present. Changing the client clears the date
   * first (see applyClient), so the new client's terms fill a blank rather
   * than overwriting the previous client's answer in place.
   */
  useEffect(() => {
    if (!selectedClient || selectedClient.paymentTermsDays <= 0) return;
    setForm((f) => {
      if (f.dueDate || f.clientId !== selectedClient.id) return f;
      return {
        ...f,
        dueDate: new Date(
          new Date(f.invoiceDate).getTime()
            + selectedClient.paymentTermsDays * 86_400_000,
        )
          .toISOString()
          .slice(0, 10),
      };
    });
  }, [selectedClient]);

  const selectedPlacement = placements.data?.items.find((p) => p.id === form.placementId);

  /**
   * Which of the client's GST registrations this invoice bills.
   *
   * A registration is per entity per state, and the one billed decides the
   * tax -- the document compares the seller's state to the buyer's, and the
   * buyer's state belongs to the registration rather than to the client.
   */
  const registrations = useQuery<GstRegistration[]>({
    queryKey: ['client', form.clientId, 'gst-registrations'],
    enabled: Boolean(form.clientId),
    queryFn: async () =>
      (await api.get(`/api/v1/clients/${form.clientId}/gst-registrations`)).data,
  });
  const registrationRows = registrations.data ?? [];

  /**
   * Confirmed by a person, every time.
   *
   * Not part of `form`: it is not submitted, it is the gate on submitting.
   * Whether place of supply follows the billed registration or the location
   * the service was rendered is a question for the owner's CA and is not
   * settled, so nothing here may decide it quietly -- a default that is wrong
   * and unremarked puts the client's reclaim at odds with the document, and
   * nothing on either would say so.
   */
  const [gstConfirmed, setGstConfirmed] = useState(false);

  /**
   * The suggestion, and it is only a suggestion.
   *
   * Two rules, in order, and both are things a person already decided rather
   * than inferences this code invented:
   *
   * 1. The registration whose state appears in the placement's work location.
   *    Matched on the text because `location` is free text -- "Gurugram",
   *    "Haryana", "Gurugram, Haryana" -- and there is no city-to-state table
   *    in this product. A city that does not name its state simply does not
   *    match, which is the honest outcome.
   * 2. Otherwise the one somebody marked default.
   *
   * No match leaves the picker empty rather than guessing. The line under it
   * says which rule fired, so the suggestion can be disagreed with.
   */
  const suggested = (() => {
    const where = (selectedPlacement?.location ?? '').toLowerCase();
    const byLocation = where
      ? registrationRows.find((r) => where.includes(r.state.toLowerCase()))
      : undefined;
    if (byLocation) return { row: byLocation, why: 'work location' as const };
    const byDefault = registrationRows.find((r) => r.isDefault);
    if (byDefault) return { row: byDefault, why: 'marked default' as const };
    return null;
  })();

  // Fills a blank, never overwrites a choice -- the same rule the due date
  // above follows. Re-confirmation is required whenever the selection moves,
  // including when this effect moves it.
  useEffect(() => {
    if (!suggested) return;
    setForm((f) =>
      f.clientGstRegistrationId ? f : { ...f, clientGstRegistrationId: suggested.row.id },
    );
  }, [suggested?.row.id]);

  useEffect(() => {
    setGstConfirmed(false);
  }, [form.clientGstRegistrationId]);

  /** Something to bill against, and nobody has said it is the right one. */
  const gstUnconfirmed =
    Boolean(form.clientGstRegistrationId) && !gstConfirmed;

  const previewTotal = (Number(form.amount) || 0) + (Number(form.taxAmount) || 0);

  /**
   * Choosing a client clears what belonged to the last one.
   *
   * The placement, because the API refuses an invoice pointing at another
   * client's placement; and the due date, so the effect above can fill it from
   * the new client's terms rather than leaving the previous client's answer
   * standing under a different name.
   */
  const applyClient = (clientId: string) => {
    setForm((f) => ({ ...f, clientId, placementId: '', dueDate: '' }));
  };

  return (
    <form
      className="card space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (overPaying || gstUnconfirmed) return;
        onSubmit(
          {
            clientId: form.clientId,
            placementId: form.placementId || null,
            clientGstRegistrationId: form.clientGstRegistrationId || null,
            invoiceNumber: form.invoiceNumber.trim(),
            invoiceDate: form.invoiceDate,
            dueDate: form.dueDate || null,
            amount: form.amount,
            taxAmount: form.taxAmount || '0.00',
            notes: form.notes.trim() || null,
          },
          // Null unless money was actually entered. An empty box is not a
          // receipt of zero, and the endpoint refuses one anyway.
          hasPayment
            ? {
                amount: received,
                tdsWithheld: tdsWithheld.trim() || '0',
                receivedOn,
              }
            : null,
        );
      }}
    >
      {(title || onClose) && (
        <div className="-mt-1 flex items-start justify-between gap-3 border-b border-gray-200 pb-3">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          {onClose && (
            <button
              type="button"
              className="btn-ghost p-1"
              aria-label="Close"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="i-client">Client</label>
          <ClientSelect
            id="i-client"
            purpose="billing"
            value={form.clientId}
            onChange={applyClient}
            attachedId={form.clientId || null}
            required
            placeholder="Select…"
          />
        </div>

        <div>
          <label className="label" htmlFor="i-placement">
            Placement {requirePlacement ? '' : '(optional)'}
          </label>
          <select
            id="i-placement"
            className="input"
            value={form.placementId}
            onChange={(e) => set('placementId', e.target.value)}
            disabled={!form.clientId}
            required={requirePlacement}
          >
            <option value="">
              {requirePlacement ? 'Choose a placement…' : 'Not against a placement'}
            </option>
            {placements.data?.items.map((p) => (
              <option key={p.id} value={p.id}>
                {titleCaseName(p.candidateName) ?? p.id} · {p.position ?? 'role not recorded'}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Which registration is billed, and a person saying so.

          Only shown where the client has registrations on file. A client with
          none bills against Client.gstin and Client.state exactly as before,
          which is what every invoice raised so far has done -- putting an
          empty picker on those would be a control that cannot be used. */}
      {form.clientId && registrationRows.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <label className="label" htmlFor="i-gst-reg">
            Bill against which GST registration
          </label>
          <select
            id="i-gst-reg"
            className="input"
            value={form.clientGstRegistrationId}
            onChange={(event) => set('clientGstRegistrationId', event.target.value)}
          >
            <option value="">Not against a registration</option>
            {registrationRows.map((r) => (
              <option key={r.id} value={r.id}>
                {r.gstin} · {r.state}
                {r.label ? ` · ${r.label}` : ''}
              </option>
            ))}
          </select>

          {/* Why this one is offered, so the suggestion can be disagreed
              with. A suggestion nobody can see the reasoning for is
              indistinguishable from a rule. */}
          {suggested && suggested.row.id === form.clientGstRegistrationId && (
            <p className="mt-1 text-[11px] text-amber-800">
              {suggested.why === 'work location'
                ? `Offered because the work location names ${suggested.row.state}.`
                : 'Offered because it is the registration marked default.'}
            </p>
          )}
          {!form.clientGstRegistrationId && (
            <p className="mt-1 text-[11px] text-amber-800">
              Nothing matched the work location, so nothing is pre-selected.
            </p>
          )}

          <label className="mt-2 flex items-start gap-2 text-xs text-amber-900">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={gstConfirmed}
              onChange={(event) => setGstConfirmed(event.target.checked)}
              disabled={!form.clientGstRegistrationId}
            />
            <span>
              This is the registration to bill. It sets the GSTIN on the
              document and decides CGST/SGST against IGST, and both are frozen
              once the invoice is raised.
            </span>
          </label>
        </div>
      )}

      {snapshot && <Snapshot facts={snapshot} note={snapshotNote} />}

      {selectedPlacement && (
        <PlacementContext placement={selectedPlacement} onUseExpected={(v) => set('amount', v)} />
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="i-number">Invoice Number</label>
          <input
            spellCheck={false}
            id="i-number"
            className="input font-mono"
            placeholder="HTS/25-26/P/003"
            value={form.invoiceNumber}
            onChange={(e) => set('invoiceNumber', e.target.value)}
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            Must be unique within this company.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label" htmlFor="i-date">Invoice Date</label>
            <input
              id="i-date"
              type="date"
              className="input"
              value={form.invoiceDate}
              onChange={(e) => set('invoiceDate', e.target.value)}
              required
            />
          </div>
          <div>
            <label className="label" htmlFor="i-due">Due Date</label>
            <input
              id="i-due"
              type="date"
              className="input"
              value={form.dueDate}
              min={form.invoiceDate}
              onChange={(e) => set('dueDate', e.target.value)}
            />
            {selectedClient && selectedClient.paymentTermsDays > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                {selectedClient.paymentTermsDays}-day terms
              </p>
            )}
          </div>
        </div>

        <div>
          <label className="label" htmlFor="i-amount">Amount (Before Tax)</label>
          <input
            spellCheck={false}
            id="i-amount"
            className="input font-mono"
            inputMode="decimal"
            placeholder="0.00"
            value={form.amount}
            onChange={(e) => set('amount', e.target.value)}
            readOnly={figuresLocked}
            required
          />
        </div>
        <div>
          <label className="label" htmlFor="i-tax">Tax</label>
          <input
            spellCheck={false}
            id="i-tax"
            className="input font-mono"
            inputMode="decimal"
            value={form.taxAmount}
            onChange={(e) => set('taxAmount', e.target.value)}
            readOnly={figuresLocked}
          placeholder="11340.00"
          />
        </div>
      </div>

      {figuresLocked && lockReason && (
        <p className="flex items-start gap-1.5 rounded bg-gray-50 px-3 py-2 text-xs text-gray-600">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {lockReason}
        </p>
      )}

      <p className="text-sm text-gray-600">
        Total <strong className="tabular-nums">{money(previewTotal.toFixed(2))}</strong>
        <span className="ml-2 text-xs text-gray-500">
          computed by the server from amount plus tax
        </span>
      </p>

      <div>
        <label className="label" htmlFor="i-notes">Notes</label>
        <textarea
          spellCheck={true}
          id="i-notes"
          className="input"
          rows={2}
          value={form.notes}
          onChange={(e) => set('notes', e.target.value)}
        placeholder="Anything the client needs to see on the invoice."
        />
      </div>

      {statusOverride && (
        <div className="rounded-xl bg-gray-50 p-3 ring-1 ring-gray-200">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-gray-900">Status</h4>
            <span
              className={`badge ${STATUS_TONE[statusOverride.status]} ${
                statusOverride.overridden ? OVERRIDDEN_RING : ''
              }`}
            >
              {statusLabel(statusOverride.status)}
              {statusOverride.overridden && ' · overridden'}
            </span>
          </div>

          {/* Both numbers, whenever they disagree. The whole design is that
              the arithmetic keeps being true about the payments and the
              decision sits beside it, so a reader can tell which is which. */}
          {statusOverride.overridden && (
            <p className="mt-1.5 text-xs text-gray-600">
              The payments say{' '}
              <span className="font-medium text-gray-900">
                {statusOverride.derivedStatus
                  ? statusLabel(statusOverride.derivedStatus)
                  : 'nothing yet'}
              </span>
              {statusOverride.reason && <> — overridden because “{statusOverride.reason}”</>}
            </p>
          )}

          <div className="mt-2 grid gap-3 sm:grid-cols-3">
            <div>
              <label className="label" htmlFor="i-override">Override to</label>
              <select
                id="i-override"
                className="input"
                value={overrideTo}
                onChange={(e) => setOverrideTo(e.target.value)}
                disabled={statusOverride.saving}
              >
                <option value="">Leave it to the payments</option>
                {OVERRIDABLE.map((value) => (
                  <option key={value} value={value}>
                    {statusLabel(value)}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="label" htmlFor="i-override-reason">Reason</label>
              <input
                id="i-override-reason"
                className="input"
                placeholder="Why the status is not what the payments say"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                disabled={statusOverride.saving || !overrideTo}
              />
            </div>
          </div>

          {/* Required, and said before the save rather than as a 422 after it.
              An unexplained override is a moved receivable nobody downstream
              can account for, which is the failure this feature exists to
              avoid rather than one to reproduce more tidily. */}
          <p className="mt-1.5 text-xs text-gray-500">
            {overrideTo
              ? 'A reason is required — it is the only record of why a receivable moved.'
              : 'Clearing the override hands this invoice back to what the payments say.'}
          </p>

          <button
            type="button"
            className="btn-secondary mt-2 text-xs"
            disabled={
              statusOverride.saving
              || (Boolean(overrideTo) && !overrideReason.trim())
            }
            onClick={() =>
              statusOverride.onApply(
                (overrideTo || null) as InvoiceStatus | null,
                overrideReason.trim(),
              )
            }
          >
            {statusOverride.saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {overrideTo ? 'Override status' : 'Clear override'}
          </button>
        </div>
      )}

      {payment && (
        <div className="rounded-xl bg-gray-50 p-3 ring-1 ring-gray-200">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-gray-900">Payment Received</h4>
            <span className="tabular-nums text-xs text-gray-500">
              {money(payment.outstanding)} outstanding
            </span>
          </div>

          {!payment.enabled ? (
            /* Shown and explained rather than hidden. record_payment refuses a
               draft outright, so live boxes here could only fail -- but
               removing them silently would leave somebody hunting for where
               payments went. */
            <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-800">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {payment.disabledReason}
            </p>
          ) : (
            <>
              <div className="mt-2 grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="label" htmlFor="i-received">Received Amount</label>
                  <input
                    spellCheck={false}
                    id="i-received"
                    className="input font-mono"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={received}
                    onChange={(e) => setReceived(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label" htmlFor="i-tds-withheld">TDS Withheld</label>
                  <input
                    spellCheck={false}
                    id="i-tds-withheld"
                    className="input font-mono"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={tdsWithheld}
                    onChange={(e) => setTdsWithheld(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label" htmlFor="i-received-on">Payment Date</label>
                  <input
                    id="i-received-on"
                    type="date"
                    className="input"
                    value={receivedOn}
                    min={payment.invoiceDate}
                    onChange={(e) => setReceivedOn(e.target.value)}
                  />
                </div>
              </div>

              <p className="mt-2 text-xs text-gray-500">
                TDS sits beside the amount rather than being deducted from it.
                Both count towards settling the invoice, so a receipt short by
                exactly the withholding still closes it. Leave the amount blank
                to save the invoice without recording a receipt.
              </p>

              {overPaying && (
                <p className="mt-2 rounded bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  {money(settles.toFixed(2))} settles more than the{' '}
                  {money(payment.outstanding)} outstanding. Over-payment is
                  refused -- record what actually arrived, or correct the
                  invoice.
                </p>
              )}
            </>
          )}
        </div>
      )}

      <button
        type="submit"
        className="btn-primary"
        disabled={submitting || overPaying || gstUnconfirmed}
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        {submitLabel}
      </button>
    </form>
  );
}

/**
 * What the placement says, beside what is being invoiced.
 *
 * Expected and invoiced are shown next to each other but never reconciled
 * automatically — they legitimately differ, and a guarantee period that has
 * not expired is a reason a human might bill less, or not at all.
 */
function PlacementContext({
  placement,
  onUseExpected,
}: {
  placement: Placement;
  onUseExpected: (value: string) => void;
}) {
  const guaranteeOpen =
    placement.guaranteeUntil !== null
    && new Date(placement.guaranteeUntil) >= new Date(today());

  return (
    <div className="rounded-xl bg-gray-50 p-3 ring-1 ring-gray-200">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-700">
          Expected from this placement{' '}
          <strong className="tabular-nums">{money(placement.billingAmount)}</strong>
        </p>
        {placement.billingAmount && (
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => onUseExpected(placement.billingAmount as string)}
          >
            Use this amount
          </button>
        )}
      </div>
      <p className="mt-1 text-xs text-gray-500">
        Status {placement.status}
        {placement.dateOfJoining && ` · joined ${placement.dateOfJoining}`}
      </p>

      {guaranteeOpen && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Replacement guarantee runs to {placement.guaranteeUntil}. A drop inside
          that window usually voids billing — this does not stop you invoicing,
          it is here so the decision is a deliberate one.
        </p>
      )}
    </div>
  );
}

/** The facts the invoice freezes when it is raised. */
export type SnapshotFacts = {
  candidateName: string | null;
  candidatePhone: string | null;
  position: string | null;
  dateOfJoining: string | null;
  location: string | null;
  ctcAnnual: string | null;
  recruiterName: string | null;
  feeBasis: FeeBasis | null;
  feePercent: string | null;
};

/**
 * The placement's facts, shown and not editable.
 *
 * Read-only because these are snapshot fields: the server copies them onto the
 * invoice at raise, and from then on the invoice keeps what was true then even
 * if the candidate record moves. Letting them be typed here would mean an
 * invoice could say something the placement never did.
 *
 * Rendered as text rather than as disabled inputs. A greyed box still looks
 * like somewhere to type, and reads as a field that is broken rather than one
 * that is decided elsewhere.
 */
function Snapshot({ facts, note }: { facts: SnapshotFacts; note?: string }) {
  const fee =
    facts.feeBasis === 'flat'
      ? 'Flat'
      : facts.feeBasis === 'ctc_percent'
        ? facts.feePercent
          ? facts.feePercent + '% of CTC'
          : '% of CTC'
        : null;

  const rows: [string, string | null][] = [
    ['Candidate', titleCaseName(facts.candidateName)],
    ['Mobile', formatPhone(facts.candidatePhone)],
    ['Position', facts.position],
    ['Date of joining', facts.dateOfJoining],
    ['Location', facts.location],
    ['CTC', facts.ctcAnnual ? money(facts.ctcAnnual) : null],
    ['Fee basis', fee],
    ['Recruiter', titleCaseName(facts.recruiterName)],
  ];

  return (
    <div className="rounded-xl bg-gray-50 p-3 ring-1 ring-gray-200">
      <div className="flex items-center gap-1.5">
        <Lock className="h-3.5 w-3.5 text-gray-400" />
        <h4 className="text-sm font-semibold text-gray-900">From the Placement</h4>
      </div>
      <dl className="mt-2 grid gap-x-4 gap-y-1.5 sm:grid-cols-2">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-3 text-xs">
            <dt className="text-gray-500">{label}</dt>
            <dd className="text-right text-gray-900">
              {value ?? <span className="text-gray-300">not recorded</span>}
            </dd>
          </div>
        ))}
      </dl>
      {note && <p className="mt-2 text-xs text-gray-500">{note}</p>}
    </div>
  );
}
